import React from 'react';
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  ExitToApp as ExitToAppIcon,
} from '@mui/icons-material';
import {
  Button,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import toast from 'react-hot-toast';
import AdminLayoutView from 'src/Layout/AdminLayout/AdminLayoutView';
import AdminTabs from 'src/Layout/AdminLayout/AdminTabs';
import { AdminTabValue } from 'src/Layout/AdminLayout/AdminTabs/types';
import AdminViewWrapper from 'src/Layout/AdminLayout/AdminViewWrapper';
import useConfirmationDialog from 'src/components/ConfirmationDialog/view';
import { CopyToClipboardButton } from 'src/components/CopyToClipboardButton';
import GridData from 'src/components/GridData/GridData';
import { StatusChip, Dropdown } from 'src/UIKit';
import { TableHeader, TableHeaderItem, TABLE_HEADER_ACTION_SX } from 'src/components/TableHeader/TableHeader';
import { getFullDate } from 'src/helpers/date';
import { AdminCompany } from 'src/modules/companies/types';
import { useAdminCompaniesViewController } from 'src/modules/companies/controllers';
import { FREE_CREDITS_HINT } from 'src/modules/companies/helpers';
import { CreateAdminCompanyFormDialog } from 'src/modules/companies/components/CreateAdminCompanyForm/CreateAdminCompanyFormDialog';

const CREATED_BY_OPTIONS = [
  { value: "", title: "Anyone" },
  { value: "self_service", title: "Self-service" },
  { value: "admin", title: "Admin" },
];

// The dropdown speaks in three words and the API in three values, one of which is "no filter at all".
const createdByValue = (selfService?: boolean) =>
  selfService === undefined ? "" : selfService ? "self_service" : "admin";

const createdByFilter = (value: string) => (value === "" ? undefined : value === "self_service");

export const AdminCompaniesView = () => {
  const {
    navigate,
    isPageLoading,
    currentCompanyId,
    companiesFilter,
    companyList,
    searchValue,
    changeCompanyFilterForm,
    changeCompaniesSearchFilter,
    changeCompaniesSorting,
    changeCompaniesPagination,
    switchCompany,
    removeCompany,
    applicationOptions,
    createAdminCompanyFormDialogController,
    reloadCompanies,
  } = useAdminCompaniesViewController();
  const { openDialog, ConfirmationDialog } = useConfirmationDialog();

  const handleDeleteCompany = (company: AdminCompany) => {
    if (company.subscription?.status === 'active') {
      toast.error('This company has an active subscription. Delete the subscription first.');
      return;
    }

    openDialog({
      title: `Delete "${company.name}"`,
      content: "Are you sure you want to delete this company?",
      onConfirm: () => removeCompany(company.id),
    });
  };

  return (
    <>
      <ConfirmationDialog />

      <CreateAdminCompanyFormDialog
        {...createAdminCompanyFormDialogController}
        applicationOptions={applicationOptions}
        reloadCompanies={reloadCompanies}
      />

      <AdminViewWrapper>
        <AdminLayoutView tabs={<AdminTabs value={AdminTabValue.companies} />}>
          <GridData
            header={
              <TableHeader
                searchValue={searchValue}
                onSearchChange={changeCompaniesSearchFilter}
                filters={
                  <>
                    <TableHeaderItem>
                      <Dropdown
                        label="Application"
                        value={companiesFilter.applicationIds}
                        options={[{ value: "", title: "" }, ...applicationOptions]}
                        onChange={(_, v) => changeCompanyFilterForm({ applicationIds: v })}
                        margin="none"
                      />
                    </TableHeaderItem>

                    <TableHeaderItem>
                      <Dropdown
                        label="Created by"
                        value={createdByValue(companiesFilter.selfService)}
                        options={CREATED_BY_OPTIONS}
                        onChange={(_, v) => changeCompanyFilterForm({ selfService: createdByFilter(v) })}
                        margin="none"
                      />
                    </TableHeaderItem>
                  </>
                }
                actions={
                  <Button
                    onClick={() => createAdminCompanyFormDialogController.open()}
                    variant="contained"
                    sx={TABLE_HEADER_ACTION_SX}
                  >
                    Create New Company
                  </Button>
                }
              />
            }
            isLoading={isPageLoading}
            rows={companyList?.items}
            columns={[
              {
                field: "name",
                headerName: "Company",
                renderCell: (r) => {
                  return (
                    <Stack>
                      <Typography variant="body2" noWrap>
                        {r.formattedValue}
                      </Typography>

                      <Stack direction="row" alignItems="center">
                        <Typography variant="body2" color="text.disabled" noWrap>
                          {r.id}
                        </Typography>

                        <CopyToClipboardButton text={r.row.id} variant="icon" />
                      </Stack>
                    </Stack>
                  );
                },
                minWidth: 200,
                sortable: true,
                editable: false,
                flex: 1.5,
              },
              {
                field: "selfService",
                headerName: "Created by",
                renderCell: (r) => {
                  // Centred against the row rather than left to the cell: the chip is taller than a line of
                  // text, and a cell that aligns its content to the top hangs it above the neighbours.
                  return (
                    <Stack height="100%" justifyContent="center" alignItems="flex-start" gap={0.25}>
                      {r.row.selfService ? (
                        <>
                          <StatusChip label="Self-service" color="primary" />
                          {/* The step is news only while the funnel runs: on a finished company it is noise. */}
                          {r.row.selfServiceStep !== "done" && (
                            <Typography variant="caption" color="text.disabled" noWrap>
                              {r.row.selfServiceStep}
                            </Typography>
                          )}
                        </>
                      ) : (
                        <Typography variant="caption" color="text.disabled">Admin</Typography>
                      )}
                    </Stack>
                  );
                },
                minWidth: 120,
                sortable: false,
                editable: false,
                flex: 1,
              },
              {
                field: "status",
                headerName: "Status",
                initVisibility: false,
                minWidth: 100,
                sortable: false,
                editable: false,
                flex: 1,
              },
              {
                field: "phoneNumbers",
                headerName: "Phone Numbers",
                renderCell: (r) => {
                  return r.row.phoneNumbers.map((m) => m.number).join(",");
                },
                minWidth: 100,
                sortable: false,
                editable: false,
                flex: 1,
              },
              {
                field: "messagingProfileId",
                headerName: "Messaging Profile ID",
                renderCell: (r) => {
                  return (
                    <Stack direction="row" alignItems="center" width="100%" display="inline-flex">
                      <Typography variant="caption" noWrap>
                        {r.row.messagingProfileId}
                      </Typography>

                      {r.row.messagingProfileId && <CopyToClipboardButton text={r.row.messagingProfileId} variant="icon" />}
                    </Stack>
                  );
                },
                minWidth: 150,
                sortable: false,
                editable: false,
                flex: 1,
              },
              {
                field: "tcrCampaignId",
                headerName: "TCR Campaign ID",
                renderCell: (r) => {
                  return (
                    <Stack direction="row" alignItems="center" width="100%" display="inline-flex">
                      <Typography variant="caption" noWrap>
                        {r.row.tcrCampaignId}
                      </Typography>

                      {r.row.tcrCampaignId && <CopyToClipboardButton text={r.row.tcrCampaignId} variant="icon" />}
                    </Stack>
                  );
                },
                minWidth: 100,
                sortable: false,
                editable: false,
                flex: 1,
              },
              {
                field: "application",
                headerName: "Application",
                renderCell: (r) => {
                  return r.row.application.name;
                },
                minWidth: 100,
                sortable: true,
                editable: true,
                flex: 1,
              },
              {
                field: "subscription.plan",
                headerName: "Plan",
                renderCell: (r) => {
                  return r.row.subscription?.plan?.name;
                },
                minWidth: 100,
                sortable: false,
                editable: true,
                flex: 1,
              },
              {
                field: "subscription.startDate",
                headerName: "Start Date",
                renderCell: (r) => {
                  if (r.row.subscription?.startType === 'onboarding_complete') {
                    return 'Account Setup Complete';
                  }

                  return getFullDate(r.row.subscription?.startDate);
                },
                minWidth: 150,
                sortable: false,
                editable: true,
                flex: 1,
              },
              {
                field: "subscription.autorenew",
                headerName: "Auto Renew",
                renderCell: (r) => {
                  if (r.row.subscription?.autoRenew) {
                    return <StatusChip label="On" color="success" />;
                  }

                  return <StatusChip label="Off" color="default" />;
                },
                minWidth: 80,
                sortable: false,
                editable: false,
                flex: 1,
              },
              {
                field: "manualBilling",
                headerName: "Billing Type",
                renderCell: (r) => {
                  if (r.row.manualBilling) {
                    return <StatusChip label="Manual" color="default" />;
                  }

                  return <StatusChip label="Automatic" color="primary" />;
                },
                minWidth: 100,
                sortable: false,
                editable: false,
                flex: 1,
              },
              {
                // Named after the API sort key rather than the row property: the grid builds `sort` from it.
                field: "free_credits",
                headerName: "Free Credits",
                description: FREE_CREDITS_HINT,
                renderCell: (r) => r.row.freeCreditsRemaining.toLocaleString(),
                minWidth: 100,
                sortable: true,
                editable: false,
                flex: 1,
              },
            ]}
            renderAction={(r) => {
              return (
                <>
                  <IconButton size="small" color="primary" onClick={() => navigate(`/admin/companies/edit/${r.id}`)}>
                    <EditIcon fontSize="inherit" />
                  </IconButton>

                  {currentCompanyId !== r.id && (
                    <IconButton size="small" color="primary" onClick={() => switchCompany(r.id)}>
                      <ExitToAppIcon fontSize="inherit" />
                    </IconButton>
                  )}

                  {currentCompanyId !== r.id && (
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => handleDeleteCompany(r)}
                    >
                      <DeleteIcon fontSize="inherit" />
                    </IconButton>
                  )}
                </>
              );
            }}
            sorting={{
              value: companiesFilter.sort,
              onChange: changeCompaniesSorting,
            }}
            pagination={{
              page: companiesFilter.page,
              pageSize: companiesFilter.limit,
              totalCount: companyList?.count,
              onChange: changeCompaniesPagination,
            }}
          />
        </AdminLayoutView>
      </AdminViewWrapper>
    </>
  );
};

